import React from 'react';
import { useSelector } from 'react-redux';
import { TrendingUp, AlertCircle } from 'lucide-react';
import { TraditionalLTCQuoteComparison } from './TraditionalLTCQuoteComparison';
import { InsuranceQuoteComparison } from './InsuranceQuoteComparison';
import { 
  selectIllustrationResponseDataByQuoteId, 
  selectClient
} from '@client/model/selectors';
import { AppState } from '@client/model/store';
import { 
  createUpdatedQuoteFromResponse, 
  createQuoteWithROIandProjectedCoverage,
  createTraditionalLTCQuoteWithROIandProjectedCoverage 
} from '@client/util/policyQuoteCalculations';

const parseMonetaryValue = (value: string | null): number => {
  if (!value) return 0;
  return parseFloat(value.replace(/[$,]/g, '')) || 0;
};

type IllustrationResponseData = { [key: string]: string | null } | null;

const useInsuranceQuoteData = (
  policyQuote: PolicyQuote, 
  responseData: IllustrationResponseData, 
  client: Client
) => {
  const initialPremium = policyQuote.premiumAmount || 0;
  const initialMonthlyBenefit = policyQuote.ltcBenefitMonthlyIndividual || 0;
  const initialAnnualBenefit = policyQuote.ltcBenefitAnnualIndividual || 0;
  const initialRoiData = client ? createQuoteWithROIandProjectedCoverage(policyQuote, client) : null;
  const initialROI = initialRoiData?.roi || 0;
  
  let updatedPremium = initialPremium;
  let updatedMonthlyBenefit = initialMonthlyBenefit;
  let updatedAnnualBenefit = initialAnnualBenefit;
  let updatedROI = initialROI;
  
  if (responseData && client) {
    updatedPremium = responseData.premiumAmount 
      ? parseMonetaryValue(responseData.premiumAmount) 
      : initialPremium;
    updatedMonthlyBenefit = responseData.initialMonthlyBenBase
      ? parseMonetaryValue(responseData.initialMonthlyBenBase)
      : initialMonthlyBenefit;
    updatedAnnualBenefit = responseData.initialAnnualBenBase
      ? parseMonetaryValue(responseData.initialAnnualBenBase)
      : initialAnnualBenefit;

    const updatedQuote = createUpdatedQuoteFromResponse(policyQuote, responseData);
    const updatedRoiData = createQuoteWithROIandProjectedCoverage(updatedQuote, client);
    updatedROI = updatedRoiData?.roi || initialROI;
  }
  
  const initialQuoteData = {
    premiumAmount: Math.round(initialPremium),
    monthlyLTCBenefit: Math.round(initialMonthlyBenefit),
    annualLTCBenefit: Math.round(initialAnnualBenefit),
    deathBenefit: Math.round(policyQuote.deathBenefitInitial || 0),
    totalLTCBenefit: Math.round(policyQuote.ltcBenefitTotal || 0),
    expectedROI: initialROI * 100,
  };
  
  const updatedQuoteData = {
    premiumAmount: Math.round(updatedPremium),
    monthlyLTCBenefit: Math.round(updatedMonthlyBenefit),
    annualLTCBenefit: Math.round(updatedAnnualBenefit),
    deathBenefit: Math.round(responseData?.deathBenefit ? parseMonetaryValue(responseData.deathBenefit) : policyQuote.deathBenefitInitial || 0),
    totalLTCBenefit: Math.round(responseData?.totalInitialLTC ? parseMonetaryValue(responseData.totalInitialLTC) : policyQuote.ltcBenefitTotal || 0),
    expectedROI: updatedROI * 100,
  };
  
  return { initialQuoteData, updatedQuoteData };
};

const useTraditionalLTCQuoteData = (tlcQuote: TraditionalLTCQuote, client: Client | null) => {
  const roiData = client ? createTraditionalLTCQuoteWithROIandProjectedCoverage(tlcQuote, client) : null;
  const expectedROI = roiData?.roi || 0;
  
  return { expectedROI };
};

interface UpdatedQuoteSectionProps {
  clientPolicyApplicationId: string;
  sourceQuote: PolicyQuote | TraditionalLTCQuote;
}

export const UpdatedQuoteSection: React.FC<UpdatedQuoteSectionProps> = ({
  sourceQuote,
}) => {
  const client = useSelector(selectClient);
  const isTraditionalLTC = 'traditionalLTCQuoteId' in sourceQuote;
  const quoteId = isTraditionalLTC ? sourceQuote.traditionalLTCQuoteId : sourceQuote.insuranceQuoteId;
  
  const responseData = useSelector((state: AppState) => 
    selectIllustrationResponseDataByQuoteId(state, quoteId)
  ) as IllustrationResponseData;
  
  const { initialQuoteData, updatedQuoteData } = !isTraditionalLTC 
    ? useInsuranceQuoteData(sourceQuote as PolicyQuote, responseData, client)
    : { initialQuoteData: null, updatedQuoteData: null };
    
  const { expectedROI } = isTraditionalLTC
    ? useTraditionalLTCQuoteData(sourceQuote as TraditionalLTCQuote, client)
    : { expectedROI: 0 };

  return (
    <div className="rounded-lg border-2 border-primary-100 bg-primary-50 p-6">
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary">
            <TrendingUp className="h-4 w-4 text-white" />
          </div>
          <h3 className="text-lg font-bold text-gray-900">Your Quote Has Been Updated!</h3>
        </div>
        
        <p className="text-sm text-gray-700">
          We've refined your initial estimate with a more detailed quote from the carrier. Please review the updated premium and expected ROI below.
        </p>
 
        {isTraditionalLTC ? (
          <TraditionalLTCQuoteComparison 
            quote={sourceQuote as TraditionalLTCQuote} 
            expectedROI={expectedROI} 
          />
        ) : (
          <InsuranceQuoteComparison 
            initialQuote={initialQuoteData!}
            updatedQuote={updatedQuoteData!}
            carrierName={(sourceQuote as PolicyQuote).carrierName}
          />
        )}

        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
          <p className="text-xs text-amber-900">
            <span className="font-semibold">Just a heads-up:</span> This is still an estimate before the formal underwriting review. The final price isn't locked in until the carrier reviews your application and assigns a final health class (like Preferred or Standard).
          </p>
        </div>
      </div>
    </div>
  );
};